import { useDialogIframeStyles } from "@/hooks/useDialogContainer";
import { Close as CloseIcon } from "@mui/icons-material";
import {
  Button,
  CircularProgress,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Dialog as MuiDialog,
} from "@mui/material";
import { FC } from "react";
import { DialogConfig } from "./types";

type Props = {
  config: DialogConfig;
  open: boolean;
  onClose: () => void;
  onExited: () => void;
};

/**
 * Dialog component that renders based on provided configuration.
 * Supports custom title, content, actions, and close behavior.
 */
const Dialog: FC<Props> = ({ config, open, onClose, onExited }) => {
  const iframeStyles = useDialogIframeStyles();
  const {
    title,
    content,
    showCloseButton = true,
    confirmLabel = "Confirmar",
    cancelLabel = "Cancelar",
    onConfirm,
    onCancel,
    confirming = false,
    maxWidth = "sm",
  } = config;

  const handleConfirm = async () => {
    if (onConfirm) {
      await onConfirm();
    }
    onClose();
  };

  const handleCancel = () => {
    if (onCancel) {
      onCancel();
    }
    onClose();
  };

  return (
    <MuiDialog
      open={open}
      onClose={onClose}
      maxWidth={maxWidth}
      fullWidth
      slotProps={{
        transition: {
          onExited: onExited,
        },
        paper: {
          sx: {
            borderRadius: 2,
            ...iframeStyles,
          },
        },
      }}
    >
      <DialogTitle
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          pb: 1,
        }}
      >
        {title}
        {showCloseButton && (
          <IconButton
            onClick={onClose}
            size="small"
            disabled={confirming}
            sx={{
              color: "text.secondary",
              "&:hover": {
                backgroundColor: "action.hover",
              },
            }}
          >
            <CloseIcon />
          </IconButton>
        )}
      </DialogTitle>

      <DialogContent sx={{ px: 3, pb: 2 }}>{content}</DialogContent>

      <DialogActions sx={{ px: 3, pb: 3 }}>
        {onCancel !== undefined && (
          <Button
            onClick={handleCancel}
            variant="outlined"
            disabled={confirming}
          >
            {cancelLabel}
          </Button>
        )}
        {onConfirm !== undefined && (
          <Button
            onClick={handleConfirm}
            variant="contained"
            disabled={confirming}
            startIcon={confirming ? <CircularProgress size={16} /> : null}
          >
            {confirmLabel}
          </Button>
        )}
      </DialogActions>
    </MuiDialog>
  );
};

export default Dialog;
